import Dialog from "@mui/material/Dialog";
import {TransitionProps} from "@mui/material/transitions";
import Grow from "@mui/material/Slide";
import React, {useEffect, useRef, useState} from "react";
import {Box, Button, IconButton, Stack, TextField, Typography} from "@mui/material";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import {IFetchFloorPlanBaseDto, IFloorPlanBase} from "../../../common/interfaces/floor-plan-base";
import {fetchFloorPlansBase} from "../../../api/floor-plan-base";
import {createToastNotification} from "../../../utilities/helpers";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Grow in={true} ref={ref} {...props}/>;
});

const SvgImageCell = ({source, id, encodedImgs}: any) => {
    const [url, setUrl] = useState<string | null>(null);

    useEffect(() => {

        if (!source) return;

        if (encodedImgs.current[id]) {
            setUrl(encodedImgs.current[id]);
        } else {

            fetch(source)
                .then((response) => response.text())
                .then((text) => {
                    let encodedStr = `data:image/svg+xml;base64, ${btoa(text)}`
                    setUrl(encodedStr);
                    encodedImgs.current = {...encodedImgs.current, [id]: encodedStr}
                })
                .catch((error) => console.error("Error loading SVG:", error));
        }

    }, []);

    return url ? (
        <img
            src={url}
            alt=""
        />
    ) : (
        <span>Učitavanje...</span>
    );
};

const ImageModal = ({open, handleClose, imageUrl}: { open: boolean; handleClose: () => void; imageUrl: string }) => {
    return (
        <Dialog open={open} onClose={handleClose} maxWidth={'lg'}>
            <Box
                sx={{
                    p: 4,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: 1000,
                    height: 800,
                    bgcolor: "white",
                }}
            >
                {imageUrl ? <img
                    src={imageUrl}
                    alt=""
                /> : <span>Nema slike za prikaz</span>}
            </Box>
        </Dialog>
    );
};

export const FloorPlanBaseListModal = (
    {
        isVisible,
        setIsVisible,
        setFloorPlanBase
    }: {
        isVisible:boolean,
        setIsVisible:(visible:boolean)=>void,
        setFloorPlanBase:(id:number)=>void
    }
) => {

    const [search, setSearch] = useState<string>('');
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const paginationModel = {page: page, pageSize: perPage};

    const encodedImgs = useRef({});

    const columns: GridColDef[] = [
        {field: 'id', headerName: 'ID', width: 70, sortable: false},
        {field: 'name', headerName: 'Naziv', flex: 2, sortable: false},
        {
            field: 'image',
            headerName: 'Pregled',
            sortable: false,
            flex: 3,
            cellClassName: "base-preview-cell",
            renderCell: (params) => {
                return <SvgImageCell
                    source={params.row.source}
                    id={params.row.id}
                    encodedImgs={encodedImgs}
                />
            },
        },
        {
            field: 'actions',
            headerName: '',
            width: 100,
            sortable: false,
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    component="label"
                    startIcon={<ChevronRightOutlinedIcon/>}
                    onClick={(event) => {
                        event.stopPropagation();
                        setFloorPlanBase(params.row.id);
                        setIsVisible(false);
                    }}
                >
                    Postavi
                </Button>
            ),
        },
    ];
    const [tableData, setTableData] = useState<IFloorPlanBase[]>([]);
    const [totalBases, setTotalBases] = useState<number>(0);

    const [selectedImage, setSelectedImage] = useState('');
    const [imageModalOpen, setImageModalOpen] = useState(false);

    const handleFetchFloorPlanBases = async (searchParam?: string) => {

        const data: IFetchFloorPlanBaseDto = {
            page: page,
            perPage: perPage,
        };

        if (searchParam !== undefined) {
            // @ts-ignore
            data.search = searchParam
        } else if (search.length > 0) {
            // @ts-ignore
            data.search = search
        }

        try {
            let response = await fetchFloorPlansBase(data);
            setTableData(response.bases)
            setTotalBases(response.totalItems)
        } catch (error: any) {
            createToastNotification('error', error?.response?.data?.message);
        }
    }

    // @ts-ignore
    const handleRowClick = async (params) => {
        try {
            //@ts-ignore
            setSelectedImage(encodedImgs.current[params.row.id] ?? '')
            setImageModalOpen(true)
        } catch (error: any) {
            createToastNotification('error', error?.response?.data?.message);
        }
    }

    useEffect(() => {
        if (isVisible) {
            handleFetchFloorPlanBases()
        }
    }, [page, perPage]);

    useEffect(() => {
        if (isVisible) {
            handleFetchFloorPlanBases()
        }else{
            encodedImgs.current={}
        }
    }, [isVisible]);

    return (
        <>
            <Dialog
                fullWidth={true}
                maxWidth={'xl'}
                open={isVisible}
                onClose={() => {
                    setIsVisible(false)
                }}
                TransitionComponent={Transition}
            >
                <Stack height='950px' sx={{padding: 5}}>
                    <Stack>
                        <Typography variant="h5">
                            Izaberi osnovu prostornog plana
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center">
                        <TextField
                            sx={{
                                width: '500px',
                                height: '50px',
                                marginRight: '20px',
                                '& .MuiInputBase-root': {
                                    backgroundColor: 'white',
                                    borderRadius: '5px',
                                    padding: 0,
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#d0d0d0',
                                },
                            }}
                            label="Pretraga po nazivu"
                            name="search"
                            size="small"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleFetchFloorPlanBases(search);
                                }
                            }}
                            margin="normal"
                        />
                        <Tooltip title="Pretraži">
                            <IconButton
                                onClick={() => {
                                    handleFetchFloorPlanBases()
                                }}
                                color="primary"
                                size="medium"
                                sx={{
                                    backgroundColor: '#f0f0f0',
                                    '&:hover': {
                                        backgroundColor: '#e0e0e0',
                                    },
                                }}
                            >
                                <SearchIcon/>
                            </IconButton>
                        </Tooltip>
                        {search.length > 0 &&
                            <Tooltip title="Očisti unos">
                                <IconButton
                                    onClick={() => {
                                        setSearch('')
                                        handleFetchFloorPlanBases('')
                                    }}
                                    color="error"
                                    size="medium"
                                    sx={{
                                        marginLeft: 1,
                                        backgroundColor: '#f0f0f0',
                                        '&:hover': {
                                            backgroundColor: '#e0e0e0',
                                        },
                                    }}
                                >
                                    <ClearIcon/>
                                </IconButton>
                            </Tooltip>}
                    </Stack>
                    <DataGrid
                        disableColumnMenu
                        disableRowSelectionOnClick
                        disableMultipleRowSelection
                        rows={tableData}
                        rowCount={totalBases}
                        getRowHeight={() => 200}
                        columns={columns}
                        onRowClick={handleRowClick}
                        pagination
                        paginationModel={{
                            page: paginationModel.page - 1,
                            pageSize: paginationModel.pageSize
                        }}
                        paginationMode={'server'}
                        pageSizeOptions={[5, 10, 20]}
                        //@ts-ignore
                        onPaginationModelChange={(model) => {
                            setPage(model.page + 1)
                            setPerPage(model.pageSize)
                        }}
                        sx={{
                            marginTop: 2,
                            paddingLeft: '10px',
                            paddingRight: '10px',
                            border: 0,
                            '.MuiDataGrid-cell:focus': {
                                outline: 'none',
                            },
                            '& .MuiDataGrid-cell:focus-within': {
                                outline: 'none',
                            },
                            '& .Mui-selected': {
                                backgroundColor: 'transparent !important',
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: 'transparent !important',
                            },
                            '& .base-preview-cell': {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            },
                            '& .base-preview-cell img': {
                                cursor: 'pointer',
                                width: 200,
                                height: 100,
                                objectFit: 'cover',
                            },
                        }}
                        localeText={{
                            MuiTablePagination: {
                                labelRowsPerPage: "Redova po strani",
                                labelDisplayedRows: ({from, to, count}) =>
                                    `${from} - ${to} od ${count !== -1 ? count : ` ${to}`}`,
                            },
                            noRowsLabel: "Nema dostupnih podataka",
                        }}
                    />
                </Stack>
            </Dialog>
            <ImageModal open={imageModalOpen} handleClose={() => setImageModalOpen(false)} imageUrl={selectedImage}/>
        </>
    );
}
